import { style } from "@vanilla-extract/css";
import { recipe, type RecipeVariants } from "@vanilla-extract/recipes";
import { rem } from "@huub-web/utils/css/rem";

import { transition } from "../../utils/transition";
import { theme } from "../../theme/css/theme.css";

export const containerStyle = style({});

/**
 * Applied to the containerRef div inside <Container padding="none"> above the carousel.
 * Sets --container-width to the content width of the Container (100cqi resolves to the
 * nearest container ancestor's inline size), which is then used by fillerStyle to compute
 * the exact filler: (--content-width - --container-width) / 2.
 */
export const containerMeasureStyle = style({
  vars: {
    "--container-width": "100cqi",
  },
});

export const carouselStyle = style({
  // Do not remove - used for className targeting
});

export const carouselWrapperStyle = recipe({
  base: {
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    margin: 0,
  },
  variants: {
    variant: {
      default: {},
      centered: {},
    },
  },
});

export const scrollContainerStyle = style({
  width: "100%",
  overflow: "hidden",
});

export const innerScrollStyle = style({
  fontSize: 0,
});

export const contentContainerStyle = style({});

export const contentStyle = recipe({
  base: {
    width: "max-content",
  },
  variants: {
    variant: {
      default: {
        paddingBottom: theme.spacings.xs,
        paddingTop: theme.spacings.xs,
      },
      centered: {
        paddingBottom: theme.spacings.s,
        paddingTop: theme.spacings.s,
      },
    },
  },
});

/**
 * Filler element to center the first carousel item.
 * - default: 50cqw — centers relative to the nearest container ancestor
 * - centered: 50vw — centers relative to the full viewport
 */
export const fillerStyle = recipe({
  base: {
    flexShrink: 0,
  },
  variants: {
    variant: {
      default: {
        width: `calc(var(--filler-width, calc(${theme.spacings.xs} * 2)) - calc(var(--carousel-gap, ${theme.spacings.xxs}) * 2 - ${theme.spacings.xs}))`,
        selectors: {
          "[data-measured] &": {
            transition: transition("width"),
          },
        },
      },
      centered: {
        width: "calc(50vw - var(--carousel-gap, 8px) / 2)",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export type FillerStyleVariants = RecipeVariants<typeof fillerStyle>;

export const navigationStyle = style({
  display: "inline-flex",
  gap: theme.spacings.xs,
  justifyContent: "center",
  zIndex: 2,
  position: "relative",
  padding: theme.spacings.xs,
  margin: "auto",
  borderRadius: theme.borderRadiuses.s,
});

export const navigationItemStyle = recipe({
  base: {
    position: "relative",
    height: theme.spacings.xxs,
    width: theme.spacings.xxs,
    borderRadius: rem(999),
    backgroundColor: theme.colors.defaultBorder,
    transition: transition("background-color", "width"),
    cursor: "pointer",
    ":after": {
      content: "",
      height: "calc(100% + 10px)",
      width: "calc(100% + 10px)",
      position: "absolute",
      top: "-5px",
      left: "-5px",
      cursor: "pointer",
      background: "transparent",
    },
    ":hover": {
      backgroundColor: theme.colors.primary,
    },
    ":focus-visible": {
      outline: "none",
    },
  },
  variants: {
    active: {
      true: {
        backgroundColor: theme.colors.primary,
        width: theme.spacings.s,
      },
    },
  },
});
